// ------------------------------------------------------------
// AI Attribution — per Loren Data AI Use Policy §8.2
// Tool:        Claude Code (Anthropic)
// 2026-07-15: Registered ConvertToContractFECommand handler (WI9896 Phase 4) - SangPham
// ------------------------------------------------------------
/**
 * Application Setup
 * Registers all handlers with the mediator
 * This is the composition root for dependency injection
 */

import { logger, mediator } from './core';
import type { IHttpClient } from '@/application/core';
import { GetSampleDataQueryHandler } from './queries/sampleData';
import { GetUserContextQueryHandler } from './queries/userContext';
import { ValidateAccessCodeQueryHandler } from './queries/developerRegistration';
import { SubmitDeveloperRegistrationCommandHandler } from './commands/developerRegistration';
import { LogoutCommandHandler } from './commands/auth';
import { GetTopNotificationsQueryHandler } from './queries/notifications';
import { GetFavouritesQueryHandler } from './queries/favourites';
import {
  AddFavouriteCommandHandler,
  RemoveFavouriteCommandHandler,
} from './commands/favourites';
import { GetNetworkTenantsQueryHandler } from './queries/networkTenants';
import { GetSidebarMenuQueryHandler } from './queries/sidebarMenu';
import { GetPortalLayoutQueryHandler } from './queries/portalLayout';
import { GetDeveloperPlanTenantsQueryHandler } from './queries/developerPlan';
import { ExtendDeveloperTrialFECommandHandler } from './commands/developerPlan';
import { SyncDeveloperNetworksFECommandHandler } from './commands/developerPlan';
import { ConvertToContractFECommandHandler } from './commands/developerPlan';
import { GetSimplifyDeveloperPlansQueryHandler } from './queries/simplifyDeveloperPlan';
import { ExtendSimplifyTrialFECommandHandler } from './commands/simplifyDeveloperPlan';
import { GetSubscriptionInfoQueryHandler } from './queries/subscription';
import { InitiateUpgradeCommandHandler } from './commands/subscription';
import { GetRegistrationSessionsQueryHandler } from './queries/registrationSessions/GetRegistrationSessionsQuery';
import { RetryRegistrationFECommandHandler } from './commands/retryRegistration/RetryRegistrationCommand';
import { GetUserPreferencesQueryHandler } from './queries/userPreferences';
import { UpdateUserPreferencesCommandHandler } from './commands/userPreferences';

/**
 * Initialize the application by registering all handlers
 * Call this once at application startup
 *
 * @param httpClient - HTTP client implementation (e.g. apiClient from infrastructure layer).
 *                     Caller in src/react/index.ts must pass apiClient explicitly.
 */
export function initializeApplication(httpClient: IHttpClient): void {
  logger.info('Application', 'Initializing application...');

  // Register query handlers
  mediator.register('GetSampleDataQuery', new GetSampleDataQueryHandler(httpClient));
  mediator.register('GetUserContextQuery', new GetUserContextQueryHandler(httpClient));

  // Developer Registration command
  mediator.register(
    'SubmitDeveloperRegistrationCommand',
    new SubmitDeveloperRegistrationCommandHandler(httpClient)
  );
  mediator.register('ValidateAccessCodeQuery', new ValidateAccessCodeQueryHandler(httpClient));

  // Notifications queries
  mediator.register('GetTopNotificationsQuery', new GetTopNotificationsQueryHandler(httpClient));

  // Favourites queries
  mediator.register('GetFavouritesQuery', new GetFavouritesQueryHandler(httpClient));

  // Favourites commands
  mediator.register('AddFavouriteCommand', new AddFavouriteCommandHandler(httpClient));
  mediator.register('RemoveFavouriteCommand', new RemoveFavouriteCommandHandler(httpClient));

  // Network Tenants queries
  mediator.register('GetNetworkTenantsQuery', new GetNetworkTenantsQueryHandler(httpClient));

  // Sidebar Menu queries
  mediator.register('GetSidebarMenuQuery', new GetSidebarMenuQueryHandler(httpClient));

  // Portal Layout queries
  mediator.register('GetPortalLayoutQuery', new GetPortalLayoutQueryHandler(httpClient));

  // Developer Plan queries & commands
  mediator.register('GetDeveloperPlanTenantsQuery', new GetDeveloperPlanTenantsQueryHandler(httpClient));
  mediator.register('ExtendDeveloperTrialFECommand', new ExtendDeveloperTrialFECommandHandler(httpClient));
  mediator.register('SyncDeveloperNetworksFECommand', new SyncDeveloperNetworksFECommandHandler(httpClient));
  mediator.register('ConvertToContractFECommand', new ConvertToContractFECommandHandler(httpClient));

  // Simplify Developer Plans queries & commands
  mediator.register('GetSimplifyDeveloperPlansQuery', new GetSimplifyDeveloperPlansQueryHandler(httpClient));
  mediator.register('ExtendSimplifyTrialFECommand', new ExtendSimplifyTrialFECommandHandler(httpClient));

  // Subscription queries & commands
  mediator.register('GetSubscriptionInfoQuery', new GetSubscriptionInfoQueryHandler(httpClient));
  mediator.register('InitiateUpgradeCommand', new InitiateUpgradeCommandHandler(httpClient));

  // Auth commands
  mediator.register('LogoutCommand', new LogoutCommandHandler(httpClient));

  // Registration Sessions queries & commands
  mediator.register('GetRegistrationSessionsQuery', new GetRegistrationSessionsQueryHandler(httpClient));
  mediator.register('RetryRegistrationFECommand', new RetryRegistrationFECommandHandler(httpClient));

  // User Preferences queries & commands
  mediator.register('GetUserPreferencesQuery', new GetUserPreferencesQueryHandler(httpClient));
  mediator.register('UpdateUserPreferencesCommand', new UpdateUserPreferencesCommandHandler(httpClient));

  logger.info('Application', 'Handlers registered:', [
    'GetSampleDataQuery',
    'GetUserContextQuery',
    'SubmitDeveloperRegistrationCommand',
    'ValidateAccessCodeQuery',
    'GetTopNotificationsQuery',
    'GetFavouritesQuery',
    'AddFavouriteCommand',
    'RemoveFavouriteCommand',
    'GetNetworkTenantsQuery',
    'GetSidebarMenuQuery',
    'GetPortalLayoutQuery',
    'GetDeveloperPlanTenantsQuery',
    'ExtendDeveloperTrialFECommand',
    'SyncDeveloperNetworksFECommand',
    'ConvertToContractFECommand',
    'GetSubscriptionInfoQuery',
    'InitiateUpgradeCommand',
    'LogoutCommand',
    'GetRegistrationSessionsQuery',
    'RetryRegistrationFECommand',
    'GetUserPreferencesQuery',
    'UpdateUserPreferencesCommand',
    'GetSimplifyDeveloperPlansQuery',
    'ExtendSimplifyTrialFECommand',
  ]);

  // Add more query handler registrations here
  // mediator.register('GetUsersQuery', new GetUsersQueryHandler(httpClient));

  // Add command handler registrations here
  // mediator.register('CreateUserCommand', new CreateUserCommandHandler(httpClient));
}

/**
 * Reset the application state (useful for testing)
 */
export function resetApplication(): void {
  mediator.clear();
}
